// GL6 — aktivasyon bağlantısı SONUCU: dispatch özeti (§5.4) + kopyalanabilir bağlantı + son geçerlilik.
// Eski EP davet çekmecesi ve SP sonuç panelinin ortak gövdesi. Durum metinle taşınır (renk/ikon destek);
// `text-warning` tek başına duran glifte YASAK (açık temada 1.55:1). `activationLink` LOGLANMAZ — yalnız
// ekranda gösterilir / panoya kopyalanır; bileşen unmount olunca (yeni oluştur / kapat) bellekten düşer.
import { Check, Copy, Mail, MessageSquare, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ActivationLinkResult,
  type InviteContactChannel,
  type InviteDispatch,
  inviteDispatchLines,
} from '../api/contractors';
import { currentCulture } from '../i18n/i18n';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { Input } from '../ui/Input';
import { cn } from '../ui/cn';

const DISPATCH_KEYS: Record<InviteContactChannel, { sent: string; failed: string }> = {
  Email: { sent: 'personnelLink.dispatch.emailSent', failed: 'personnelLink.dispatch.emailFailed' },
  Sms: { sent: 'personnelLink.dispatch.smsSent', failed: 'personnelLink.dispatch.smsFailed' },
};
const CHANNEL_ICONS: Record<InviteContactChannel, typeof Mail> = {
  Email: Mail,
  Sms: MessageSquare,
};

function formatExpiry(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(currentCulture(), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

function DispatchSummary({ dispatch }: { dispatch: InviteDispatch }) {
  const { t } = useTranslation();
  if (!dispatch.emailSent && !dispatch.smsSent) {
    return (
      <p className="flex items-start gap-2 text-sm text-fg">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-fg" aria-hidden="true" />
        <span>{t('personnelLink.dispatch.none')}</span>
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {inviteDispatchLines(dispatch).map((line) => {
        const Icon = line.sent ? CHANNEL_ICONS[line.channel] : TriangleAlert;
        return (
          <li key={line.channel} className="flex items-start gap-2 text-sm text-fg">
            <Icon
              className={cn('mt-0.5 h-4 w-4 shrink-0', line.sent ? 'text-success' : 'text-fg')}
              aria-hidden="true"
            />
            <span>{t(DISPATCH_KEYS[line.channel][line.sent ? 'sent' : 'failed'])}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function ActivationLinkResultPanel({ result }: { result: ActivationLinkResult }) {
  const { t } = useTranslation();
  const linkId = useId();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  async function copyLink() {
    try {
      // activationLink loglanmaz — yalnız panoya.
      await navigator.clipboard.writeText(result.activationLink);
      setCopyError(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      setCopyError(true);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <DispatchSummary dispatch={result.dispatch} />
      <Field htmlFor={linkId} label={t('personnelLink.field.link')}>
        <div className="flex items-center gap-2">
          <Input
            id={linkId}
            readOnly
            value={result.activationLink}
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono text-xs"
          />
          <Button type="button" variant="secondary" onClick={copyLink} className="shrink-0">
            {copied ? (
              <Check className="h-4 w-4 text-success" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {copied ? t('personnelLink.copied') : t('personnelLink.copy')}
          </Button>
        </div>
      </Field>
      <output className="sr-only">{copied ? t('personnelLink.copied') : ''}</output>
      {copyError ? <Alert tone="danger">{t('personnelLink.copyFailed')}</Alert> : null}
      <p className="text-xs text-muted">
        {t('personnelLink.expiresAt', { date: formatExpiry(result.expiresAt) })}
      </p>
    </div>
  );
}
